import { useEffect, type ReactNode } from 'react';
import { useAuth } from '../auth/authContext';
import { DeskContext } from './deskContext';
import type { DeskEngine } from './engine';
import { DESK_CHANNEL } from './broadcast';

const LOCK_NAME = 'namibiago-desk';

/**
 * Binds the desk engine to the signed-in session (business build only):
 *
 * - One desk per browser: the tab holding the `namibiago-desk` Web Lock owns
 *   the persisted queue. Other tabs are followers (the check-in screen tells
 *   them to use the other tab) — two tabs persisting two copies of the queue
 *   into one IndexedDB would overwrite each other's events.
 * - `start(userId)` on sign-in opens the encrypted vault, hydrates the paused
 *   queue and resumes it (or raises the unsynced-events blocker).
 * - A wipe in any tab is broadcast so every tab drops its in-memory copy
 *   (sign-out-all incl. other tabs). SIGNED_OUT itself reaches every tab via
 *   supabase-js's own cross-tab channel → AuthProvider → engine.
 */
export function DeskProvider({ engine, children }: { engine: DeskEngine; children: ReactNode }) {
  const { session } = useAuth();
  const userId = session?.user.id ?? null;

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.locks) {
      // No Web Locks (very old browser / test env): act as the only tab.
      void engine.setLeader(true);
      return;
    }
    void engine.setLeader(false);
    const abort = new AbortController();
    let release: (() => void) | null = null;
    navigator.locks
      .request(LOCK_NAME, { signal: abort.signal }, () => {
        void engine.setLeader(true);
        return new Promise<void>((resolve) => {
          release = resolve;
        });
      })
      .catch(() => undefined);
    return () => {
      abort.abort();
      release?.();
    };
  }, [engine]);

  useEffect(() => {
    if (userId) void engine.start(userId);
  }, [engine, userId]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(DESK_CHANNEL);
    channel.onmessage = (message: MessageEvent) => {
      if (message.data === 'wiped') engine.handleRemoteWipe();
    };
    return () => channel.close();
  }, [engine]);

  return <DeskContext.Provider value={engine}>{children}</DeskContext.Provider>;
}

